/**
 * Formatação visual de telefones por país. Espelho TS de
 * `apps/android/.../core/phone/PhoneFormatter.kt`.
 *
 * Pares (digitsExpected, máscara humanizada) — não tenta validar variações
 * regionais (móvel vs fixo, formatos antigos): isso fica no backend via
 * libphonenumber-js. Aqui só deixa a digitação confortável.
 *
 * Comprimento esperado é apenas dos dígitos nacionais — o `dialCode` do país
 * é prefixado depois para gerar E.164.
 */
import type { Country } from './enums';

export type PhoneSpec = {
  /** Máximo de dígitos nacionais aceitos (corte do input). */
  expectedDigits: number;
  /**
   * Mínimo de dígitos para o número ser considerado "completo" o suficiente
   * para enviar. Para país com máscara conhecida = `expectedDigits` (tamanho
   * exato). Para país do catálogo (genérico) = limiar frouxo — quem valida o
   * formato de fato é libphonenumber no servidor.
   */
  minDigits: number;
  /** Hint visual mostrando o formato (ex.: "(11) 91234-5678"). */
  placeholder: string;
  /** Recebe os dígitos puros e devolve string formatada. */
  format: (digits: string) => string;
  /** Prefixo internacional (ex.: "+55"). */
  dialCode: string;
};

const DIAL_CODES: Readonly<Record<Country, string>> = {
  BR: '+55',
  MX: '+52',
  US: '+1',
  CA: '+1',
  IT: '+39',
};

function brFormat(d: string): string {
  let out = '(';
  out += d.slice(0, 2);
  if (d.length > 2) out += ') ' + d.slice(2, 7);
  if (d.length > 7) out += '-' + d.slice(7, 11);
  return out;
}

function mxFormat(d: string): string {
  let out = d.slice(0, 2);
  if (d.length > 2) out += ' ' + d.slice(2, 6);
  if (d.length > 6) out += ' ' + d.slice(6, 10);
  return out;
}

function usFormat(d: string): string {
  let out = '(' + d.slice(0, 3);
  if (d.length > 3) out += ') ' + d.slice(3, 6);
  if (d.length > 6) out += '-' + d.slice(6, 10);
  return out;
}

function itFormat(d: string): string {
  let out = d.slice(0, 3);
  if (d.length > 3) out += ' ' + d.slice(3, 6);
  if (d.length > 6) out += ' ' + d.slice(6, 10);
  return out;
}

const SPECS: Readonly<Record<Country, PhoneSpec>> = {
  BR: { expectedDigits: 11, minDigits: 11, placeholder: '(11) 91234-5678', format: brFormat, dialCode: DIAL_CODES.BR },
  MX: { expectedDigits: 10, minDigits: 10, placeholder: '55 1234 5678',    format: mxFormat, dialCode: DIAL_CODES.MX },
  US: { expectedDigits: 10, minDigits: 10, placeholder: '(555) 123-4567',  format: usFormat, dialCode: DIAL_CODES.US },
  CA: { expectedDigits: 10, minDigits: 10, placeholder: '(555) 123-4567',  format: usFormat, dialCode: DIAL_CODES.CA },
  IT: { expectedDigits: 10, minDigits: 10, placeholder: '320 123 4567',    format: itFormat, dialCode: DIAL_CODES.IT },
};

const SPECS_BY_CODE = new Map<string, PhoneSpec>(Object.entries(SPECS));

/**
 * Limites de um número nacional genérico (país do catálogo, sem máscara
 * compilada). E.164 admite até 15 dígitos no total; o nacional cabe folgado
 * em 15. O piso de 6 só evita enviar lixo óbvio — libphonenumber no servidor
 * é quem valida o formato real.
 */
const GENERIC_MAX_DIGITS = 15;
const GENERIC_MIN_DIGITS = 6;

export const PhoneFormatter = {
  specFor(country: Country): PhoneSpec {
    return SPECS[country];
  },
  /**
   * Spec maskless para um país do catálogo (DDI vindo do Firestore). Identidade
   * no `format` (digitação livre, sem máscara nacional) — o app não conhece o
   * formato do país novo, então não tenta humanizar. Usado quando o país NÃO
   * tem máscara compilada.
   */
  genericSpec(dialCode: string): PhoneSpec {
    return {
      expectedDigits: GENERIC_MAX_DIGITS,
      minDigits: GENERIC_MIN_DIGITS,
      placeholder: '',
      format: (d: string): string => d,
      dialCode,
    };
  },
  /**
   * Spec de um país por código ISO + DDI. País com máscara compilada
   * (BR/MX/US/CA/IT) usa a máscara nacional; qualquer outro (catálogo) cai na
   * `genericSpec(dialCode)` — sem máscara, DDI do catálogo.
   */
  specForCode(code: string, dialCode: string): PhoneSpec {
    return SPECS_BY_CODE.get(code) ?? PhoneFormatter.genericSpec(dialCode);
  },
  /** Concatena dial code + dígitos para E.164 (ex.: "+5511987654321"). */
  toE164(country: Country, digits: string): string {
    return `${DIAL_CODES[country]}${digits.replace(/\D/g, '')}`;
  },
  /**
   * Monta E.164 a partir de um DDI explícito (do catálogo) + dígitos. Versão
   * país-agnóstica de `toE164` — o caller resolve o DDI (catálogo ou compilado)
   * e passa aqui, então funciona para país novo sem entrada em `DIAL_CODES`.
   */
  e164(dialCode: string, digits: string): string {
    return `${dialCode}${digits.replace(/\D/g, '')}`;
  },
  digitsOnly(input: string): string {
    return input.replace(/\D/g, '');
  },
  dialCodeFor(country: Country): string {
    return DIAL_CODES[country];
  },
};
